import {FC, useState} from 'react';
import {toast} from 'react-toastify';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {deleteBrand, updateOrganization, VerdocsEndpoint, type IBrand} from '@verdocs/js-sdk';
import {
  DataTable,
  DataTableBody,
  DataTableDataCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableHeaderRow,
  DataTableRow,
  FlexFill,
  HelpDialog,
  PageTitle,
  Spinner,
} from '../../Components';
import {invalidateQuery, useBrands, useOrganization} from '../../react-sdk/queries';
import {CreateBrandDialog} from '../../Dialogs/CreateBrandDialog';
import {OkCancelDialog} from '../../Dialogs/OkCancelDialog';
import {BrandDetail} from './BrandDetail';
import {AppState} from '../../AppState';
import {StarIcon as StarOutline} from '@heroicons/react/24/outline';
import {StarIcon as StarSolid, PencilSquareIcon, TrashIcon} from '@heroicons/react/24/solid';

const StatusBadge: FC<{status: string | null | undefined}> = ({status}) => {
  if (!status) return <span className="text-gray-400 italic text-xs">Not configured</span>;
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    verified: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    suspended: 'bg-gray-100 text-gray-800',
  };
  return <span className={`${styles[status] || 'bg-gray-100 text-gray-800'} text-xs font-medium px-2.5 py-0.5 rounded`}>{status}</span>;
};

export const Branding: FC = () => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandsQuery = useBrands(organizationId);
  const orgQuery = useOrganization(organizationId);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState<IBrand | null>(null);
  const [working, setWorking] = useState(false);

  const brands = brandsQuery.data || [];
  const defaultBrandId = orgQuery.data?.default_brand_id;

  if (selectedBrandId) {
    return <BrandDetail brandId={selectedBrandId} onBack={() => setSelectedBrandId(null)} />;
  }

  const handleSetDefault = async (brand: IBrand) => {
    const newDefault = defaultBrandId === brand.id ? null : brand.id;
    setWorking(true);
    try {
      await updateOrganization(VerdocsEndpoint.getDefault(), organizationId, {default_brand_id: newDefault} as any);
      invalidateQuery(['organization']);
      toast.success(newDefault ? `${brand.name || brand.key} set as default` : 'Default brand cleared');
    } catch (e: any) {
      toast.error(`Error updating default brand: ${e.message}`);
    }
    setWorking(false);
  };

  const handleDelete = async () => {
    if (!deletingBrand) return;
    setWorking(true);
    try {
      await deleteBrand(VerdocsEndpoint.getDefault(), organizationId, deletingBrand.id);
      invalidateQuery(['brands']);
      toast.success('Brand deleted');
    } catch (e: any) {
      toast.error(`Error deleting brand: ${e.response?.data?.error || e.message}`);
    }
    setDeletingBrand(null);
    setWorking(false);
  };

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <PageTitle>
        Branding
        <FlexFill />
        <HelpDialog title="Branding">
          <p className="mb-4">
            Brands let you customize the visual identity, email domain, and signing experience for your organization. You can create multiple
            brands for different product lines or sub-brands.
          </p>
          <p>Each brand can have its own logos, colors, custom email domain, and CSS overrides to fully white-label the signing experience.</p>
        </HelpDialog>
      </PageTitle>

      {brandsQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner mode="dark" />
        </div>
      ) : brands.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <p className="text-lg mb-2">No brands configured</p>
          <p className="text-sm mb-6">Create a brand to customize logos, colors, email domains, and the signing experience.</p>
          <VerdocsButton label="Create Brand" size="normal" onClick={() => setCreating(true)} />
        </div>
      ) : (
        <>
          <DataTable>
            <DataTableHead>
              <DataTableHeaderRow>
                <DataTableHeaderCell className="w-10"></DataTableHeaderCell>
                <DataTableHeaderCell>Name</DataTableHeaderCell>
                <DataTableHeaderCell className="hidden tablet:table-cell">Key</DataTableHeaderCell>
                <DataTableHeaderCell className="hidden tablet:table-cell">Email Domain</DataTableHeaderCell>
                <DataTableHeaderCell className="w-24">Actions</DataTableHeaderCell>
              </DataTableHeaderRow>
            </DataTableHead>
            <DataTableBody>
              {brands.map((brand) => (
                <DataTableRow key={brand.id}>
                  <DataTableDataCell>
                    <button
                      onClick={() => handleSetDefault(brand)}
                      className="text-gray-400 hover:text-yellow-500 transition-colors"
                      title={defaultBrandId === brand.id ? 'Remove as default' : 'Set as default'}>
                      {defaultBrandId === brand.id ? <StarSolid className="size-5 text-yellow-500" /> : <StarOutline className="size-5" />}
                    </button>
                  </DataTableDataCell>
                  <DataTableDataCell>
                    <button onClick={() => setSelectedBrandId(brand.id)} className="text-[#654dcb] hover:underline font-medium">
                      {brand.name || brand.key}
                    </button>
                  </DataTableDataCell>
                  <DataTableDataCell className="hidden tablet:table-cell font-mono text-sm text-gray-500">{brand.key}</DataTableDataCell>
                  <DataTableDataCell className="hidden tablet:table-cell">
                    <StatusBadge status={brand.email_domain_status} />
                  </DataTableDataCell>
                  <DataTableDataCell>
                    <div className="flex flex-row gap-2">
                      <button onClick={() => setSelectedBrandId(brand.id)} className="text-gray-400 hover:text-[#654dcb]" title="Edit">
                        <PencilSquareIcon className="size-5" />
                      </button>
                      <button onClick={() => setDeletingBrand(brand)} className="text-gray-400 hover:text-red-600" title="Delete">
                        <TrashIcon className="size-5" />
                      </button>
                    </div>
                  </DataTableDataCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>

          <div className="mt-4">
            <VerdocsButton label="Create Brand" size="normal" onClick={() => setCreating(true)} />
          </div>
        </>
      )}

      {creating && (
        <CreateBrandDialog
          onClose={() => setCreating(false)}
          onCreated={(brand) => {
            setCreating(false);
            invalidateQuery(['brands']);
            setSelectedBrandId(brand.id);
          }}
        />
      )}

      {deletingBrand && (
        <OkCancelDialog onOk={handleDelete} onCancel={() => setDeletingBrand(null)}>
          <p className="mb-6">
            Are you sure you want to delete the brand <strong>{deletingBrand.name || deletingBrand.key}</strong>? This cannot be undone.
          </p>
        </OkCancelDialog>
      )}

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}
    </div>
  );
};
